import { useEffect, useRef, useCallback } from "react"
import { useSearchParams } from "react-router-dom"
import { useDispatch } from "react-redux"
import { useMutation } from "@tanstack/react-query"
import { get } from "lodash"
import { Creators as ApiWrapperCreators } from "model/apiWrapper"
import { usePostQuery } from "hooks/queries"
import { useAccountId } from "resources/account"
import { log } from "helpers/logger"
import { getPanelIdForModule } from "../helpers/module-renderers"
import { MODULE_TYPES } from "../modules/types"
import { GRAPH_PREFERENCE_BASE } from "../store/graph-slice"
import { ASSET_VIEW_PREFERENCE_BASE } from "../store/asset-view-slice"
import { KPI_PREFERENCE_SUBTYPE_BASE } from "../store/kpi-slice"
import { FACTORS_VIEW_PREFERENCE_BASE } from "../store/factors-view-slice"
import { getDashboardPreferenceKey, getDashboardPreferenceFallbackKey } from "../helpers/preference-keys"

const CLEAR_PREFERENCES_PARAM = "clearPreferences"
const PREFERENCE_TYPE = "dashboard"
const MODULES_SUBTYPE = "modules"

// List of all flat (non-panel-scoped) dashboard preference subtypes to clear.
const DASHBOARD_PREFERENCE_SUBTYPES = [
  "dashboard",
  "asset-view",
  "kpi",
  "factors-view",
  "graph",
  "modules",
  "optimization",
  "dockviewLayout",
  "model",
  "trade",
]

// Per-panel subtype matching each sync hook's ownSubType — all panels use "." (getPanelIdForModule
// is the same id the sync hooks persist under).
function getPerPanelSubtype(module) {
  const panelId = getPanelIdForModule(module)
  switch (module.type) {
    case MODULE_TYPES.GRAPH:
      return `${GRAPH_PREFERENCE_BASE}.${panelId}`
    case MODULE_TYPES.ASSET_VIEW:
      return `${ASSET_VIEW_PREFERENCE_BASE}.${panelId}`
    case MODULE_TYPES.KPI:
      return `${KPI_PREFERENCE_SUBTYPE_BASE}.${panelId}`
    case MODULE_TYPES.FACTORS:
      return `${FACTORS_VIEW_PREFERENCE_BASE}.${panelId}`
    default:
      return null
  }
}

/**
 * Hook that clears all dashboard preferences when the URL contains the clearPreferences parameter
 *
 * This hook should be used at the top level of the dashboard, BEFORE any preference loading occurs.
 * When ?clearPreferences is detected (with or without a value):
 * 1. The saved module list is read so per-panel keys (graph.{panelId}, asset-view.{panelId}, …)
 *    can be enumerated — the flat subtypes alone miss those.
 * 2. All dashboard preferences (flat + per-panel) are set to empty objects.
 * 3. The page reloads with the clearPreferences param removed.
 *
 * Scope: per-panel keys are enumerated from the panels in the saved `dashboard.modules`
 * record. A panel removed from the layout before clearing leaves its `graph.{id}` /
 * `kpi.{id}` key behind — harmless dead data (new panels get fresh uuid-derived ids and
 * never read it), not a rehydration risk. A true wipe of every orphaned per-panel key
 * would need a server-side prefix delete.
 *
 * Clears the preferences of the dashboard identified by `dashboardId` (its scoped key, plus the
 * legacy unscoped key when this is the default dashboard — otherwise the read-side legacy
 * fallback would resurrect the cleared values).
 *
 * @param {string} dashboardId - The dashboard whose preferences to clear
 * @returns {{ isClearing: boolean }} - Whether preferences are currently being cleared
 */
export function useClearDashboardPreferences(dashboardId) {
  const [searchParams] = useSearchParams()
  const dispatch = useDispatch()
  const accountId = useAccountId()
  // Latch per dashboardId, not a boolean: the layout stays mounted across param-only
  // navigations, so a boolean latched for one dashboard would skip clearing the next.
  const clearedForDashboardRef = useRef(null)

  const fallbackKey = getDashboardPreferenceFallbackKey(dashboardId)
  const keysToClear = [getDashboardPreferenceKey(dashboardId), ...(fallbackKey ? [fallbackKey] : [])]

  // Check if the param exists (with or without a value)
  // Both ?clearPreferences and ?clearPreferences=true will trigger clearing
  const shouldClear = searchParams.has(CLEAR_PREFERENCES_PARAM)

  // Read the saved modules only when clearing, to enumerate per-panel keys — the flat
  // subtypes can't cover them, so they'd survive the clear and rehydrate on reload.
  // Modules under the scoped AND legacy keys are merged: panels saved under either key
  // contribute per-panel subtypes that must be cleared under both.
  const modulesQuery = usePostQuery({
    url: "/api/users/me/preferences",
    payload: {
      items: keysToClear.map((key) => ({ accountId, key, type: `${PREFERENCE_TYPE}.${MODULES_SUBTYPE}` })),
    },
    key: ["preferences", "clear-enumerate", accountId, dashboardId].join(":"),
    enabled: shouldClear && Boolean(accountId),
    select: (queryResult) => (queryResult ?? []).flatMap((row) => get(row, "value.modules", [])),
  })

  const removeParamAndReload = useCallback(() => {
    const url = new URL(window.location.href)
    url.searchParams.delete(CLEAR_PREFERENCES_PARAM)
    window.location.replace(url.toString())
  }, [])

  const clearPreferencesMutation = useMutation({
    mutationFn: async (modules) => {
      const perPanelSubtypes = (modules ?? []).map(getPerPanelSubtype).filter(Boolean)
      const allSubtypes = [...new Set([...DASHBOARD_PREFERENCE_SUBTYPES, ...perPanelSubtypes])]
      const clearPromises = keysToClear.flatMap((key) => allSubtypes.map((subType) => {
        const payload = {
          items: [{
            accountId,
            key,
            type: `${PREFERENCE_TYPE}.${subType}`,
            value: {}, // empty object clears the stored prefs
          }],
        }
        return new Promise((resolve, reject) => {
          dispatch(ApiWrapperCreators.put("/api/users/me/preferences", payload, resolve, reject))
        })
      }))

      return Promise.all(clearPromises)
    },
    onSuccess: () => {
      removeParamAndReload()
    },
    onError: (error) => {
      log(error)
      // Still remove the param and reload to prevent infinite loop
      removeParamAndReload()
    },
  })

  useEffect(() => {
    // Only clear once per dashboard, and only if the param is present
    if (!shouldClear || clearedForDashboardRef.current === dashboardId || !accountId) {
      return
    }

    // Wait for the modules read to settle before clearing. Accept the error terminal state
    // too, so a failed read clears the flat subtypes instead of hanging.
    if (!modulesQuery.isSuccess && !modulesQuery.isError) {
      return
    }

    clearedForDashboardRef.current = dashboardId
    clearPreferencesMutation.mutate(modulesQuery.data ?? [])
  }, [
    shouldClear,
    dashboardId,
    accountId,
    modulesQuery.isSuccess,
    modulesQuery.isError,
    modulesQuery.data,
    clearPreferencesMutation,
  ])

  return {
    isClearing: shouldClear,
  }
}
